import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../styles/Welcome.module.css';
import videoBackground from '../styles/background.mp4';

/* ─── REAL FTS DATA ─────────────────────────────────────── */

const features = [
  {
  
    title: 'AI Load Entry',
    desc: 'Upload a Rate Confirmation PDF and let FTS auto-fill every load field instantly — shipper, consignee, rate, dates, and more. No manual data entry.',
  },
  {

    title: 'Driver Pay & Payroll',
    desc: 'Generate driver settlements in seconds — per mile, hourly, fixed salary, or percentage of load. Full employee payroll for dispatchers, accountants, and office staff.',
  },
  {

    title: 'Invoicing & Factoring',
    desc: 'Auto-generate client invoices from completed loads. Send directly by email or forward full invoice packages (BOL, RC, POD) to your factoring company in one click.',
  },
  {
 
    title: 'Fuel Reports & IFTA',
    desc: 'Log fuel purchases, import carrier CSV files, and generate quarterly IFTA compliance reports. Export to PDF or Excel with full per-driver and per-truck breakdown.',
  },
  {

    title: 'Real-Time Dashboards',
    desc: 'Live KPI dashboards per driver, truck, and load. Monitor revenue, expenses, open invoices, and profit margins. Balance sheet shows every dollar in and out of your business.',
  },
  {

    title: 'Fleet & Asset Management',
    desc: 'Track trucks and trailers: insurance expiry, maintenance deadlines, inspection dates, leasing costs, and IFTA taxes. Alarm notifications before anything expires.',
  },
  {
  
    title: 'Mobile Driver App',
    desc: 'Drivers share real-time GPS, scan and upload documents directly to dispatcher chat, receive loads, access secure broker links, and communicate via text and files.',
  },
  {

    title: 'Load Management',
    desc: 'Full TMS — add, edit, assign, and track loads with status updates, document attachments, and dispatcher notes. Complete load history per driver and truck.',
  },
  {
 
    title: 'Banking & Payments',
    desc: 'Connect your business bank account for live transaction feeds, automatic categorization, and direct payments to drivers and vendors — without leaving FTS.',
  },
];

const stats = [
  { value: '500+',  label: 'Active Fleets' },
  { value: '10K+',  label: 'Loads Managed' },
  { value: '99.8%', label: 'Uptime' },
  { value: '4.9★',  label: 'Customer Rating' },
];

const plans = [
  {
    name: 'Basic',
    price: 'FREE',
    highlight: false,
    perks: [
      'Manual payroll creation',
      'Expense summaries',
      'Driver pay overview',
     
    ],
  },
  {
    name: 'Standard',
    price: '$9.99/mo',
    highlight: false,
    perks: [
      'Up to 3 drivers & trucks',
      'Fuel report management',
      'IFTA manual entry & export',
      'Branded Driver Pays',
      'PDF & Excel export',
    ],
  },
  {
    name: 'Premium',
    price: '$19.99/mo',
    highlight: true,
    perks: [
      'Up to 5 drivers & trucks',
      'Full KPI dashboard',
      'Invoice generation & email',
      'Employee payroll module',
      'Balance sheet overview',
    ],
  },
  {
    name: 'Ultimate',
    price: '$99.99/mo',
    highlight: false,
    perks: [
      'Unlimited drivers & trucks',
      'Real-time banking integration',
      'Automated payments',
      'Auto-invoice on load completion',
      'Priority support',
    ],
  },
   {
    name: 'Enterprise',
    price: '$499.99/mo',
    highlight: false,
    perks: [
      'Unlimited drivers & trucks',
      'Real-time banking integration',
      'Automated payments',
      'Auto-invoice on load completion',
      'Priority support',
    ],
  },
];

const testimonials = [
  {
    name: 'Marcus T.',
    role: 'Owner — 28 Trucks',
    text: 'FTS cut our dispatch time in half. The AI load entry alone saves my team 3 hours a day. The factoring module replaced a whole separate software we were paying for.',
  },
  {
    name: 'Elena R.',
    role: 'Operations Manager',
    text: 'Finally a platform that understands trucking. Driver pays used to take us all Friday — now we generate them in 10 minutes. IFTA reporting is completely automated.',
  },
  {
    name: 'David K.',
    role: 'Fleet Owner — 60+ Units',
    text: 'The balance sheet module gave us financial visibility we never had before. We can see profit per load, expenses per truck, and our live bank balance — all in one screen.',
  },
];

/* ─── COMPONENT ─────────────────────────────────────────── */

const WelcomePage = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen]               = useState(false);
  const [scrolled, setScrolled]                   = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [visibleSections, setVisibleSections]     = useState(new Set());
  const sectionRefs = useRef([]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const t = setInterval(
      () => setActiveTestimonial(p => (p + 1) % testimonials.length),
      4800
    );
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries =>
        entries.forEach(entry => {
          if (entry.isIntersecting)
            setVisibleSections(prev => new Set([...prev, entry.target.dataset.section]));
        }),
      { threshold: 0.12 }
    );
    sectionRefs.current.forEach(r => r && observer.observe(r));
    return () => observer.disconnect();
  }, []);

  const addRef    = (el, i) => { sectionRefs.current[i] = el; };
  const closeMenu = () => setIsMenuOpen(false);

  return (
    <div className={styles.welcomePageContainer}>

      {/* ─── VIDEO BACKGROUND ─── */}
      <video className={styles.welcomePageVideoBg} autoPlay loop muted playsInline>
        <source src={videoBackground} type="video/mp4" />
      </video>

      {/* ════════════ HEADER ════════════ */}
      <header className={`${styles.welcomePageHeader} ${scrolled ? styles.welcomePageHeaderScrolled : ''}`}>

        <div className={styles.welcomePageLogo} onClick={() => navigate('/')}>
          Full Trucking Service
        </div>

        <div
          className={styles.welcomePageHamburger}
          onClick={() => setIsMenuOpen(v => !v)}
          aria-label="Toggle navigation"
        >
          <span className={`${styles.welcomePageHamburgerLine} ${isMenuOpen ? styles.welcomePageHamburgerLineOpen : ''}`} />
          <span className={`${styles.welcomePageHamburgerLine} ${isMenuOpen ? styles.welcomePageHamburgerLineOpen : ''}`} />
          <span className={`${styles.welcomePageHamburgerLine} ${isMenuOpen ? styles.welcomePageHamburgerLineOpen : ''}`} />
        </div>

        <nav className={`${styles.welcomePageNav} ${isMenuOpen ? styles.welcomePageNavOpen : ''}`}>
          <ul className={styles.welcomePageNavList}>
            <li className={styles.welcomePageNavItem}    onClick={() => { navigate('/about');    closeMenu(); }}>About Us</li>
            <li className={styles.welcomePageNavItem}    onClick={() => { navigate('/services'); closeMenu(); }}>Services</li>
            <li className={styles.welcomePageNavItem}    onClick={() => { navigate('/contact');  closeMenu(); }}>Contact</li>
            <li className={styles.welcomePageNavItem}    onClick={() => { navigate('/login');    closeMenu(); }}>Log In</li>
            <li
              className={`${styles.welcomePageNavItem} ${styles.welcomePageNavItemCta}`}
              onClick={() => { navigate('/pricing'); closeMenu(); }}
            >
              Get Started
            </li>
          </ul>
        </nav>
      </header>

      {/* ════════════ HERO ════════════ */}
      <section className={styles.welcomePageHero}>
        <div className={styles.welcomePageHeroContent}>

          <div className={styles.welcomePageHeroBadge}>
            AI-Powered · Built for Trucking · 30-Day Free Trial
          </div>

          <h1 className={styles.welcomePageHeroTitle}>
            The Complete Operating System
            <span className={styles.welcomePageHeroTitleAccent}> for Modern Trucking</span>
          </h1>

          <p className={styles.welcomePageHeroSub}>
            From AI-powered load entry and driver payroll to invoicing, factoring,
            IFTA, GPS tracking, and real-time financial dashboards — FTS is everything
            your fleet needs in one platform.
          </p>

          <div className={styles.welcomePageHeroActions}>
            <button className={styles.welcomePageBtnPrimary} onClick={() => navigate('/pricing')}>
              Start Free Trial
              <span className={styles.welcomePageBtnArrow}>→</span>
            </button>
            <button className={styles.welcomePageBtnGhost} onClick={() => navigate('/login')}>
              Log In to FTS
            </button>
          </div>

          <p className={styles.welcomePageHeroNote}>
            No credit card required · Setup in under 10 minutes · Cancel any time
          </p>
        </div>

        <div className={styles.welcomePageHeroStats}>
          {stats.map((s, i) => (
            <div
              key={i}
              className={styles.welcomePageStatCard}
              style={{ animationDelay: `${0.55 + i * 0.12}s` }}
            >
              <span className={styles.welcomePageStatValue}>{s.value}</span>
              <span className={styles.welcomePageStatLabel}>{s.label}</span>
            </div>
          ))}
        </div>

        <div className={styles.welcomePageScrollIndicator}>
          <span className={styles.welcomePageScrollDot} />
        </div>
      </section>

      {/* ════════════ FEATURES ════════════ */}
      <section
        className={`${styles.welcomePageSection} ${styles.welcomePageSectionFeatures}`}
        ref={el => addRef(el, 0)}
        data-section="features"
      >
        <div className={`${styles.welcomePageSectionInner} ${visibleSections.has('features') ? styles.welcomePageSectionVisible : ''}`}>
          <span className={styles.welcomePageSectionLabel}>Platform Capabilities</span>
          <h2 className={styles.welcomePageSectionTitle}>
            9 Modules. One Login.
            <br />
            <span className={styles.welcomePageSectionTitleDim}>Zero App-Switching.</span>
          </h2>
          <p className={styles.welcomePageSectionSub}>
            FTS replaces 6+ separate tools with one deeply integrated platform.
            Every module is designed around real trucking workflows — not generic business software.
          </p>

          <div className={styles.welcomePageFeaturesGrid}>
            {features.map((f, i) => (
              <div key={i} className={styles.welcomePageFeatureCard} style={{ transitionDelay: `${i * 0.06}s` }}>
                <span className={styles.welcomePageFeatureIcon}>{f.icon}</span>
                <h3 className={styles.welcomePageFeatureTitle}>{f.title}</h3>
                <p className={styles.welcomePageFeatureDesc}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════ HOW IT WORKS ════════════ */}
      <section
        className={`${styles.welcomePageSection} ${styles.welcomePageSectionHow}`}
        ref={el => addRef(el, 1)}
        data-section="how"
      >
        <div className={`${styles.welcomePageSectionInner} ${visibleSections.has('how') ? styles.welcomePageSectionVisible : ''}`}>
          <span className={styles.welcomePageSectionLabel}>Simple Onboarding</span>
          <h2 className={styles.welcomePageSectionTitle}>Up and Running in 3 Steps</h2>

          <div className={styles.welcomePageStepsGrid}>
            {[
              {
                num: '01',
                title: 'Register & Choose a Plan',
                desc: 'Select the plan that fits your fleet size. Register free — no credit card needed. We verify your account and activate it within hours.',
              },
              {
                num: '02',
                title: 'Add Your Fleet & Team',
                desc: 'Add trucks, trailers, drivers, and dispatchers. Import existing loads if needed. FTS guides you through every step of setup.',
              },
              {
                num: '03',
                title: 'Dispatch, Pay & Invoice',
                desc: 'Start assigning loads, generating driver pays, creating invoices, and monitoring your finances — all from day one, all in one place.',
              },
            ].map((step, i) => (
              <div key={i} className={`${styles.welcomePageStepCard} ${i > 0 ? styles.welcomePageStepCardBordered : ''}`}>
                <div className={styles.welcomePageStepNum}>{step.num}</div>
                <h3 className={styles.welcomePageStepTitle}>{step.title}</h3>
                <p className={styles.welcomePageStepDesc}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════ TESTIMONIALS ════════════ */}
      <section
        className={`${styles.welcomePageSection} ${styles.welcomePageSectionTestimonials}`}
        ref={el => addRef(el, 2)}
        data-section="testimonials"
      >
        <div className={`${styles.welcomePageSectionInner} ${visibleSections.has('testimonials') ? styles.welcomePageSectionVisible : ''}`}>
          <span className={styles.welcomePageSectionLabel}>Trusted by Operators</span>
          <h2 className={styles.welcomePageSectionTitle}>Real Results. Real Companies.</h2>

          <div className={styles.welcomePageTestimonialTrack}>
            {testimonials.map((t, i) => (
              <div
                key={i}
                className={`${styles.welcomePageTestimonialCard} ${i === activeTestimonial ? styles.welcomePageTestimonialCardActive : ''}`}
              >
                <div className={styles.welcomePageTestimonialQuoteMark}>"</div>
                <p className={styles.welcomePageTestimonialText}>{t.text}</p>
                <div className={styles.welcomePageTestimonialAuthor}>
                  <div className={styles.welcomePageTestimonialAvatar}>{t.name.charAt(0)}</div>
                  <div>
                    <div className={styles.welcomePageTestimonialName}>{t.name}</div>
                    <div className={styles.welcomePageTestimonialRole}>{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className={styles.welcomePageTestimonialDots}>
            {testimonials.map((_, i) => (
              <button
                key={i}
                className={`${styles.welcomePageDot} ${i === activeTestimonial ? styles.welcomePageDotActive : ''}`}
                onClick={() => setActiveTestimonial(i)}
                aria-label={`Testimonial ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ════════════ PRICING ════════════ */}
      <section
        className={`${styles.welcomePageSection} ${styles.welcomePageSectionPricing}`}
        ref={el => addRef(el, 3)}
        data-section="pricing"
      >
        <div className={`${styles.welcomePageSectionInner} ${visibleSections.has('pricing') ? styles.welcomePageSectionVisible : ''}`}>
          <span className={styles.welcomePageSectionLabel}>Transparent Plans</span>
          <h2 className={styles.welcomePageSectionTitle}>Choose Your Plan</h2>
          <p className={styles.welcomePageSectionSub}>
            From a single owner-operator to a 100-truck enterprise.
            All plans include a 30-day free trial. Add drivers at $3.49/month each.
          </p>

          <div className={styles.welcomePagePlansGrid}>
            {plans.map((plan, i) => (
              <div key={i} className={`${styles.welcomePagePlanCard} ${plan.highlight ? styles.welcomePagePlanCardHighlight : ''}`}>
                {plan.highlight && <div className={styles.welcomePagePlanBadge}>Most Popular</div>}
                <div className={styles.welcomePagePlanName}>{plan.name}</div>
                <div className={styles.welcomePagePlanPrice}>{plan.price}</div>
                <ul className={styles.welcomePagePlanPerks}>
                  {plan.perks.map((perk, j) => (
                    <li key={j} className={styles.welcomePagePlanPerkItem}>
                      <span className={styles.welcomePagePerkCheck}>✓</span>
                      {perk}
                    </li>
                  ))}
                </ul>
                <button
                  className={plan.highlight ? styles.welcomePageBtnPrimary : styles.welcomePageBtnGhost}
                  onClick={() => navigate('/pricing')}
                >
                  {plan.name === 'Business' ? 'Contact Sales' : 'Get Started'}
                </button>
              </div>
            ))}
          </div>

          <p style={{ textAlign: 'center', marginTop: 28, fontSize: '0.85rem', color: 'var(--wp-text-muted)' }}>
            Need more? See the{' '}
            <span
              style={{ color: 'var(--wp-accent)', cursor: 'pointer', fontWeight: 600 }}
              onClick={() => navigate('/pricing')}
            >
              full pricing page →
            </span>
          </p>
        </div>
      </section>

      {/* ════════════ CTA BANNER ════════════ */}
      <section
        className={`${styles.welcomePageSection} ${styles.welcomePageSectionCta}`}
        ref={el => addRef(el, 4)}
        data-section="cta"
      >
        <div className={`${styles.welcomePageCtaBannerCard} ${visibleSections.has('cta') ? styles.welcomePageSectionVisible : ''}`}>
          <h2 className={styles.welcomePageCtaBannerTitle}>
            Ready to Run Your Fleet<br />the Smart Way?
          </h2>
          <p className={styles.welcomePageCtaBannerSub}>
            Join 500+ trucking companies already saving hours every week with FTS.
            Start your free 30-day trial — no credit card, no commitment.
          </p>
          <div className={styles.welcomePageCtaBannerActions}>
            <button className={styles.welcomePageBtnPrimary} onClick={() => navigate('/pricing')}>
              Start Free Today <span className={styles.welcomePageBtnArrow}>→</span>
            </button>
            <button className={styles.welcomePageBtnGhost} onClick={() => navigate('/login')}>
              Already have an account? Log In
            </button>
          </div>
        </div>
      </section>

      {/* ════════════ FOOTER ════════════ */}
      <footer className={styles.welcomePageFooter}>
        <a href="https://2-bfs.com/terms-and-conditions" target="_blank" rel="noopener noreferrer" className={styles.welcomePageFooterLink}>Terms of Service</a>
        <a href="https://2-bfs.com/privacy-policy"       target="_blank" rel="noopener noreferrer" className={styles.welcomePageFooterLink}>Privacy Policy</a>
        <a href="https://2-bfs.com/panda-help"            target="_blank" rel="noopener noreferrer" className={styles.welcomePageFooterLink}>Help Center</a>
      </footer>

    </div>
  );
};

export default WelcomePage;
